import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check } from "lucide-react";
import {
  OPERATING_MODE_LABELS,
  OPERATING_MODULE_LABELS,
  operatingModeApi,
  operatingModeQueryKeys,
  type OperatingMode,
  type OperatingModule,
} from "../../shared/api/operatingMode";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Cài đặt → Phòng khám → Chế độ vận hành (ADR-0048).
 *
 * One switch, not ten toggles: the module list follows from the mode, so a clinic cannot end up
 * with "encounters off but invoicing on". The page shows what each mode turns off, because turning
 * half the product off is not something anyone should discover afterwards.
 */
const MODES: OperatingMode[] = ["FULL_HIS", "PATIENT_CHANNEL"];

const DESCRIPTION: Record<OperatingMode, string> = {
  FULL_HIS:
    "Phòng khám khám bệnh, kê đơn, thu tiền và báo cáo trên Addy HIS. Đây là mặc định.",
  PATIENT_CHANNEL:
    "Phòng khám dùng HIS khác làm hệ thống chính. Addy HIS chỉ chạy kênh bệnh nhân: " +
    "Zalo Mini App, tin nhắn ZBS/email, website và lịch hẹn.",
};

const ALL_MODULES: OperatingModule[] = [
  "ENCOUNTERS",
  "PRESCRIPTIONS",
  "INVENTORY",
  "BILLING",
  "VACCINATION",
  "REPORTS",
];

export function OperatingModeSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: operatingModeQueryKeys.current(),
    queryFn: () => operatingModeApi.get(),
  });

  const [mode, setMode] = useState<OperatingMode | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (query.data) {
      setMode(query.data.mode);
      setNote(query.data.note ?? "");
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: (next: { mode: OperatingMode; note: string }) => operatingModeApi.update(next),
    onSuccess: (data) => {
      qc.setQueryData(operatingModeQueryKeys.current(), data);
      // The sidebar reads the same query, so it redraws without a reload.
      setMode(data.mode);
      setNote(data.note ?? "");
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (e) => setError(apiError(e).detail),
  });

  if (!mode) {
    return (
      <div>
        <PageHeader title="Chế độ vận hành" />
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      </div>
    );
  }

  const offModules = mode === "PATIENT_CHANNEL" ? ALL_MODULES : [];
  // Chỉ cảnh báo khi ĐANG CHUYỂN sang PATIENT_CHANNEL, không phải mỗi lần mở trang của một phòng
  // khám vốn đã ở chế độ đó — cảnh báo hiện mãi là cảnh báo không ai đọc.
  const switchingToPatientChannel =
    mode === "PATIENT_CHANNEL" && query.data?.mode !== "PATIENT_CHANNEL";
  const changed = mode !== query.data?.mode || note !== (query.data?.note ?? "");

  return (
    <div className="space-y-4">
      <PageHeader
        title="Chế độ vận hành"
        description="Addy HIS là hệ thống chính của phòng khám, hay chỉ là kênh bệnh nhân?"
      />

      <Card>
        <CardContent className="space-y-3 pt-4">
          {MODES.map((m) => (
            <label
              key={m}
              className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/40"
            >
              <input
                type="radio"
                name="operating-mode"
                className="mt-1"
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              <span>
                <span className="block text-sm font-medium">{OPERATING_MODE_LABELS[m]}</span>
                <span className="block text-sm text-muted-foreground">{DESCRIPTION[m]}</span>
              </span>
            </label>
          ))}
        </CardContent>
      </Card>

      {offModules.length > 0 && (
        <Card className="border-amber-300">
          <CardContent className="space-y-2 pt-4">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              Các phần sau sẽ tắt trên Addy HIS
            </p>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {offModules.map((m) => (
                <li key={m}>{OPERATING_MODULE_LABELS[m]}</li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              Menu bị ẩn, và các API tương ứng trả lỗi — kể cả khi gõ thẳng đường dẫn. Đó là chủ
              đích: một lượt khám hay một hoá đơn tạo ở đây mà hệ thống chính không biết sẽ làm
              lệch đối soát. Dữ liệu đã có <strong>không bị xoá</strong>; đổi lại chế độ là thấy lại.
            </p>
            <p className="text-sm text-muted-foreground">
              Vẫn chạy bình thường: Lịch hẹn · Bệnh nhân · Tin nhắn · Website · Zalo Mini App ·
              Đánh giá · Cài đặt.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-2 pt-4">
          <label className="block text-sm font-medium" htmlFor="operating-note">
            Lý do (nên ghi)
          </label>
          <textarea
            id="operating-note"
            className="w-full rounded-md border p-2 text-sm"
            rows={2}
            value={note}
            placeholder="VD: phòng khám dùng HIS MQ Solutions, Addy chạy mini app + nhắc lịch"
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            Sáu tháng nữa, người đọc cần biết vì sao nửa hệ thống tắt.
          </p>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* #18965 — "cuộc trao đổi cần có TRƯỚC, không phải sau".
          Chuyển sang PATIENT_CHANNEL không xoá gì, và chuyển lại là thấy lại. Nhưng nếu phòng
          khám đã lỡ khám/xuất hoá đơn trong Addy thì phần dữ liệu đó lập tức không còn đường vào
          từ giao diện, và người bấm nút cần biết điều đó trước khi bấm chứ không phải lúc kế toán
          đi tìm. */}
      {switchingToPatientChannel && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Trước khi chuyển: phòng khám đã dùng Addy để khám hoặc xuất hoá đơn chưa?
          </p>
          <p className="mt-1 text-amber-900/90 dark:text-amber-200/90">
            Nếu đã có, những lượt khám / hoá đơn đó <strong>không bị xoá</strong> nhưng sẽ không
            còn mở được từ giao diện cho tới khi chuyển về “Addy là hệ thống chính”. Hãy đối chiếu
            với hệ thống chính của phòng khám trước, rồi mới chuyển.
          </p>
          <label className="mt-2 flex items-center gap-2 text-amber-900 dark:text-amber-200">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            Tôi đã kiểm tra và xác nhận chuyển chế độ
          </label>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          disabled={
            !changed || save.isPending || (switchingToPatientChannel && !acknowledged)
          }
          onClick={() => save.mutate({ mode, note })}
        >
          Lưu
        </Button>
        {savedAt > 0 && !changed && (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Check className="h-4 w-4" /> Đã lưu
          </span>
        )}
      </div>
    </div>
  );
}
